var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import styled from '@emotion/styled';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { SearchResultRow } from './SearchResultRow';
import { AutoComplete, Form, Input, InputNumber, Spin, } from '@robinpowered/ui-kit';
import { InviteeRow } from './InviteeRow';
import { useInviteeSearch, } from '../../hooks/useInviteeSearch';
import { isEmailAddress } from '../../utils/utils';
import { useMemo } from 'react';
import { useEventComposerGlobalContext } from '../../composers/EventComposerGlobalContext';
import { useInviteesWorkingLocation } from '../../hooks/useInviteesWorkingLocation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@awesome.me/kit-cf858715fd/icons/classic/solid';
// Option value encoding: "<type>:<id>" for search results, "email:<addr>" for raw email input.
var encodeOptionValue = function (type, id) { return "".concat(type, ":").concat(id); };
var decodeOptionValue = function (value) {
    var colonIdx = value.indexOf(':');
    return {
        type: value.substring(0, colonIdx),
        id: value.substring(colonIdx + 1),
    };
};
export var InviteesSelector = function (_a) {
    var setAttendeeCount = _a.setAttendeeCount, attendeeCount = _a.attendeeCount, hideInvitees = _a.hideInvitees, disableInviteeSearch = _a.disableInviteeSearch, invitees = _a.invitees, inviteesWithAvailability = _a.inviteesWithAvailability, isLoadingAvailability = _a.isLoadingAvailability, spaces = _a.spaces, addInvitee = _a.addInvitee, removeInvitee = _a.removeInvitee, onSpaceSelected = _a.onSpaceSelected;
    var _b = useInviteeSearch(), keyword = _b.keyword, setKeyword = _b.setKeyword, results = _b.results, isSearching = _b.isSearching, loading = _b.loading;
    var t = useTranslation('meetingRooms').t;
    var timezone = useEventComposerGlobalContext().timezone;
    var startTime = useWatch({
        name: 'startTime',
    });
    var inviteeUserIds = useMemo(function () { return invitees.map(function (invitee) { return invitee.user_id; }); }, [invitees]);
    var getInviteeStatus = useInviteesWorkingLocation({
        userIds: inviteeUserIds,
        startTime: startTime,
        timezone: timezone,
    }).getStatusForUser;
    // Build a set of keys for already-added invitees and spaces to filter
    // duplicates out of search results.
    var existingKeys = useMemo(function () {
        return new Set(__spreadArray(__spreadArray([], __read(invitees.flatMap(function (invitee) { return __spreadArray(__spreadArray([], __read((invitee.user_id != null
            ? [encodeOptionValue('user', String(invitee.user_id))]
            : [])), false), __read((invitee.email && invitee.user_id == null
            ? [encodeOptionValue('email', invitee.email)]
            : [])), false); })), false), __read((spaces !== null && spaces !== void 0 ? spaces : []).map(function (space) {
            return encodeOptionValue('space', String(space.id));
        })), false));
    }, [invitees, spaces]);
    // Lowercase set of resource calendar emails for already-added spaces.
    // Used to suppress the raw-email fallback when the user types a resource
    // email — whether that space is already in the list (would be a duplicate)
    // or the search hasn't resolved yet (prevents misrouting it as an invitee).
    var addedSpaceResourceEmails = useMemo(function () {
        return new Set((spaces !== null && spaces !== void 0 ? spaces : [])
            .map(function (space) { var _a, _b; return (_b = (_a = space.calendar) === null || _a === void 0 ? void 0 : _a.spaceResourceEmail) === null || _b === void 0 ? void 0 : _b.toLowerCase(); })
            .filter(function (email) { return email != null; }));
    }, [spaces]);
    var filteredResults = useMemo(function () {
        return results.filter(function (r) { return !existingKeys.has(encodeOptionValue(r.type, String(r.id))); });
    }, [results, existingKeys]);
    var searchResultUserIds = useMemo(function () {
        return filteredResults.filter(function (r) { return r.type === 'user'; }).map(function (r) { return Number(r.id); });
    }, [filteredResults]);
    var _c = useInviteesWorkingLocation({
        userIds: searchResultUserIds,
        startTime: startTime,
        timezone: timezone,
    }), getSearchResultStatus = _c.getStatusForUser, searchStatusLoading = _c.loading;
    var options = useMemo(function () {
        // Hold the results back until working statuses have resolved so rows
        // render with their status instead of popping it in afterwards.
        if (searchStatusLoading) {
            return [];
        }
        var resultOptions = filteredResults.map(function (result) {
            var _a, _b;
            return ({
                label: (_b = (_a = result.name) !== null && _a !== void 0 ? _a : result.email) !== null && _b !== void 0 ? _b : '',
                value: encodeOptionValue(result.type, String(result.id)),
            });
        });
        // Append a raw email option only when:
        //   1. The input is a valid email address.
        //   2. The search has fully settled with no matching results.
        //   3. The email is not a resource calendar address for an already-added
        //      space — catches duplicates and provides an instant sync check
        //      before the API search resolves.
        var keywordLower = keyword.toLowerCase();
        if (isEmailAddress(keyword) &&
            filteredResults.length === 0 &&
            !isSearching &&
            !addedSpaceResourceEmails.has(keywordLower)) {
            resultOptions.push({
                label: keyword,
                value: encodeOptionValue('email', keyword),
            });
        }
        return resultOptions;
    }, [
        filteredResults,
        keyword,
        isSearching,
        addedSpaceResourceEmails,
        searchStatusLoading,
    ]);
    var handleSelect = function (value) {
        var _a, _b, _c, _d;
        var _e = decodeOptionValue(value), type = _e.type, id = _e.id;
        if (type === 'email') {
            addInvitee({ email: id });
            setKeyword('');
            return;
        }
        var result = results.find(function (r) { return r.type === type && String(r.id) === id; });
        if (!result)
            return;
        if (type === 'space') {
            // Space results are routed to form.spaces via the dedicated hook,
            // not stored in form.invitees.
            onSpaceSelected(Number(result.id));
            setKeyword('');
            return;
        }
        if (type === 'user') {
            addInvitee({
                user_id: Number(result.id),
                name: result.name,
                avatar: (_a = result.avatar) !== null && _a !== void 0 ? _a : '',
                email: (_b = result.email) !== null && _b !== void 0 ? _b : '',
            });
            setKeyword('');
            return;
        }
        // google_contact or microsoft_contact — invite by email address.
        addInvitee({
            name: result.name,
            email: (_c = result.email) !== null && _c !== void 0 ? _c : '',
            avatar: (_d = result.avatar) !== null && _d !== void 0 ? _d : '',
        });
        setKeyword('');
    };
    // Request-only events replace the People list with an estimated-count input;
    // it keeps its own label since the composer suppresses the collapsible header
    // in that mode.
    if (hideInvitees) {
        return (_jsx(Form.Item, { style: { marginBottom: 0 }, label: t('create_event.invitees_selector_heading_estimate'), children: _jsx(InputNumber, { min: 1, value: attendeeCount, step: 1, onChange: function (val) { return setAttendeeCount(val ? Math.round(val) : 1); } }) }));
    }
    // The "People" label, count and collapsed avatar summary are owned by the
    // wrapping ComposerSection; this renders just the section's inner content.
    return (_jsxs("div", { style: { position: 'relative' }, children: [!disableInviteeSearch && (_jsx(AutoComplete, { value: keyword, onSearch: function (value) { return setKeyword(value); }, onSelect: handleSelect, options: options, filterOption: false, autoFocus: invitees.length === 0, 
                // Autofocus only when the user just revealed this section via the
                // "+ People" button. That button is only reachable when the section
                // is collapsed, which (per `sectionOpen={!!invitees.length}`) only
                // happens with zero invitees — so an empty list at mount means a
                // user-initiated open. When invitees already exist the section is
                // open on load and we must not steal focus from the title input.
                onBlur: function () { return setKeyword(''); }, showSearch: true, placeholder: t('create_event.invitees_text_input_placeholder_text'), notFoundContent: keyword.length === 0 ? null : undefined, style: { width: '100%' }, optionRender: function (option) {
                    var optionValue = String(option.value);
                    var _a = decodeOptionValue(optionValue), type = _a.type, id = _a.id;
                    if (type === 'email') {
                        return _jsx(SearchResultRow, { email: id });
                    }
                    var result = results.find(function (r) { return r.type === type && String(r.id) === id; });
                    return (_jsx(SearchResultRow, { result: result, workingStatus: (result === null || result === void 0 ? void 0 : result.type) === 'user'
                            ? getSearchResultStatus(result.id)
                            : null }));
                }, children: _jsx(Input, { prefix: _jsx(FontAwesomeIcon, { icon: faMagnifyingGlass, size: "xs" }), suffix: loading || searchStatusLoading ? _jsx(Spin, { size: "small" }) : null, placeholder: t('create_event.invitees_text_input_placeholder_text'), onBlur: function () { return setKeyword(''); } }) })), inviteesWithAvailability.length > 0 && (_jsx(InviteesList, { children: inviteesWithAvailability.map(function (invitee) { return (_jsx(InviteeRow, { invitee: invitee, isLoading: isLoadingAvailability, removeInvitee: removeInvitee, workingStatus: getInviteeStatus(invitee.user_id) }, invitee.user_id || invitee.email)); }) }))] }));
};
var InviteesList = styled.div(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  margin-top: 8px;\n"], ["\n  margin-top: 8px;\n"])));
var templateObject_1;
